import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import {
  taskV2CurrentDateAtom,
  taskV2ViewModeAtom,
  taskV2CurrentTasksAtom,
  taskV2ShowNoTimeTasksAtom,
  taskV2SpecialModeAtom,
  taskV2ActionsAtom,
  taskV2FocusedTaskIndexAtom,
  taskV2FocusedTaskIdAtom,
  taskV2TimelineScaleAtom,
  taskV2TasksByImportanceAtom
} from '../../atoms/v3/taskV2Atoms';
import { fetchTasksAtom, updateTaskAtom, tasksAtom } from '../../atoms/v0/taskAtoms';
import { projectsAtom, fetchProjectsAtom } from '../../atoms/v0/projectAtoms';
import { layoutModeAtom } from '../../atoms/v3/uiAtoms';
import { isDarkAtom } from '../../atoms/shared/uiAtoms';
import { chatInputFocusedAtom, voiceModeAtom } from '../../atoms/v3/chatInputAtoms';
import { showUndoToastAtom, undoToastAtom, executeUndoAtom } from '../../atoms/v3/undoToastAtoms';
import { useGlobalShortcutActions } from '../../hooks/useGlobalShortcutActions';
import { TaskV2Header } from './components/tasks/TaskV2Header';
import { TaskV2List } from './components/tasks/TaskV2List';
import { ChatInputContainer } from './components/chatInput/ChatInputContainer';
import { DraggableTaskButton } from './components/tasks/DraggableTaskButton';
import { TaskDetailSheet } from './components/tasks/TaskDetailSheet';
import { TimelineScaleController } from './components/tasks/TimelineScaleController';
import { TaskItemV2List } from './components/tasks/TaskItemV2List';
import { DroppableArea } from './components/tasks/DroppableArea';
import { TaskDndContext } from './components/tasks/TaskDndContext';
import { useNavigate } from 'react-router-dom';

import { UndoToastContainer } from '../../components/v3/UndoToastContainer';
import { trackEvent } from '../../lib/analytics';
import { isTouchDevice } from '../../utils/platformUtils';

// 引导相关组件
import WelcomeProjectCreator from '../../components/v3/welcome/WelcomeProjectCreator';
import AppOnboarding from '../../components/v3/onboarding/AppOnboarding';
import { useAppOnboarding } from '../../hooks/useAppOnboarding';

/**
 * TaskV2 规划页面组件
 * 新的规划功能页面，采用无边界设计，支持等高和时间轴两种模式
 */
export const TaskV2: React.FC = () => {
  const { t } = useTranslation(['v3/tasks', 'v3/common']);
  const [isDark] = useAtom(isDarkAtom);

  // 页面状态
  const [currentDate, setCurrentDate] = useAtom(taskV2CurrentDateAtom);
  const [viewMode, setViewMode] = useAtom(taskV2ViewModeAtom);
  const [showNoTimeTasks, setShowNoTimeTasks] = useAtom(taskV2ShowNoTimeTasksAtom);
  const [specialMode, setSpecialMode] = useAtom(taskV2SpecialModeAtom);

  const [currentTasks] = useAtom(taskV2CurrentTasksAtom);
  const [allTasks] = useAtom(tasksAtom); // 完整的任务列表，用于日历密度计算
  const tasksByImportance = useAtomValue(taskV2TasksByImportanceAtom);
  const [, fetchTasks] = useAtom(fetchTasksAtom);
  const [, updateTask] = useAtom(updateTaskAtom);
  const [, fetchProjects] = useAtom(fetchProjectsAtom);
  const [projects] = useAtom(projectsAtom);
  const layoutMode = useAtomValue(layoutModeAtom);
  const navigate = useNavigate();
  const setVoiceMode = useSetAtom(voiceModeAtom);
  const { focusChatInput } = useGlobalShortcutActions();

  // 键盘导航相关状态
  const chatInputFocused = useAtomValue(chatInputFocusedAtom);
  const getActions = useSetAtom(taskV2ActionsAtom);
  const actions = getActions();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const focusedTaskIndex = useAtomValue(taskV2FocusedTaskIndexAtom);
  const focusedTaskId = useAtomValue(taskV2FocusedTaskIdAtom);

  // Undo Toast 相关状态
  const [, showUndoToast] = useAtom(showUndoToastAtom);
  const undoToastData = useAtomValue(undoToastAtom);
  const [, executeUndo] = useAtom(executeUndoAtom);

  // 任务详情弹窗状态
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isNewTaskMode, setIsNewTaskMode] = useState(false);

  // 新建任务的初始数据状态
  const [newTaskInitialData, setNewTaskInitialData] = useState<{ startAt?: string; cost?: number } | null>(null);

  // 引导相关状态
  const {
    needsOnboarding,
    isFirstTime,
    completeOnboarding,
    skipOnboarding
  } = useAppOnboarding();



  // 时间轴缩放控制
  const [timelineScale, setTimelineScale] = useAtom(taskV2TimelineScaleAtom);



  // 初始化时获取任务数据
  useEffect(() => {
    fetchTasks();
    fetchProjects();
  }, [fetchTasks, fetchProjects]);

  // 当进入特殊模式时，强制切换到列表模式
  useEffect(() => {
    if (specialMode && viewMode === 'timeline') {
      setViewMode('list');
    }
  }, [specialMode, viewMode, setViewMode]);

  // 页面加载时清理临时状态（处理刷新情况）
  useEffect(() => {
    actions.clearTemporaryVisibleTasks();
  }, []); // 只在组件挂载时执行一次

  // 将项目信息合并到当前任务中（用于显示）
  const tasksWithProjects = useMemo(() => {
    if (!projects || projects.length === 0) {
      return currentTasks;
    }

    // 创建项目映射表
    const projectsMap = projects.reduce((acc, project) => {
      acc[project.id] = project;
      return acc;
    }, {} as Record<string, any>);

    return currentTasks.map(task => {
      if (task.projectId && projectsMap[task.projectId]) {
        return {
          ...task,
          project: {
            id: projectsMap[task.projectId].id,
            title: projectsMap[task.projectId].title
          }
        };
      }
      return task;
    });
  }, [currentTasks, projects]);

  // 将项目信息合并到所有任务中（用于日历密度计算）
  const allTasksWithProjects = useMemo(() => {
    if (!projects || projects.length === 0 || !allTasks) {
      return allTasks || [];
    }

    // 创建项目映射表
    const projectsMap = projects.reduce((acc, project) => {
      acc[project.id] = project;
      return acc;
    }, {} as Record<string, any>);

    return allTasks.map(task => {
      if (task.projectId && projectsMap[task.projectId]) {
        return {
          ...task,
          project: {
            id: projectsMap[task.projectId].id,
            title: projectsMap[task.projectId].title
          }
        };
      }
      return task;
    });
  }, [allTasks, projects]);

  // 处理任务点击
  const handleTaskClick = useCallback((taskId: string) => {
    setSelectedTaskId(taskId);
    setIsNewTaskMode(false);
  }, []);

  // 处理任务完成状态切换
  const handleTaskToggle = useCallback(async (taskId: string, completed: boolean) => {
    try {
      const taskToUpdate = currentTasks.find(t => t.id === taskId);
      if (!taskToUpdate) return;

      // 记录任务状态切换事件
      trackEvent(completed ? 'task completed' : 'task uncompleted', {
        task_id: taskId,
        task_title: taskToUpdate.title.substring(0, 50) || '',
        from_taskv2_page: true,
        view_mode: viewMode
      });

      const success = await updateTask({
        id: taskId,
        done: completed,
      });

      if (success) {
        // 🎯 关键：只在 uncomplete 时添加到临时缓存
        // 这样 undone 的"完成于今天"任务会临时显示在分割线下方
        if (!completed) {
          actions.addTemporaryVisibleTask(taskId);
        }

        // 🎉 焦点保持：基于位置索引的系统天然稳定，无需任何操作！

        // 显示 undo toast
        showUndoToast({
          taskId,
          taskTitle: taskToUpdate.title,
          isCompleted: completed,
          previousState: !completed
        });

        // 如果是循环任务完成，延迟刷新任务列表以显示新生成的任务
        if (completed && taskToUpdate?.recurrenceRule && !taskToUpdate.done) {
          console.log('循环任务完成，将刷新任务列表以显示新生成的任务', {
            taskId,
            recurrenceRule: taskToUpdate.recurrenceRule
          });

          // 延迟1.5秒刷新，给后端时间生成新任务
          setTimeout(() => {
            fetchTasks();
            console.log('循环任务完成后刷新任务列表');
          }, 1500);
        }
      }
    } catch (error) {
      console.error('TaskV2: 任务状态切换失败:', error);
    }
  }, [currentTasks, updateTask, fetchTasks, viewMode, showUndoToast, actions]);

  // 根据当前状态获取新任务的初始数据
  const getNewTaskInitialData = useCallback(() => {
    // 如果有拖拽创建的初始数据，优先使用
    if (newTaskInitialData) {
      return newTaskInitialData;
    }

    if (specialMode) {
      // 特殊模式下不设置时间
      return undefined;
    }

    // 具体日期模式下，设置为当前日期的开始时间
    const taskDate = new Date(currentDate);
    taskDate.setHours(9, 0, 0, 0); // 默认设置为上午9点
    return { startAt: taskDate.toISOString() };
  }, [specialMode, currentDate, newTaskInitialData]);

  // 处理新建任务
  const handleNewTask = useCallback((initialData?: { startAt?: string; cost?: number }) => {
    setSelectedTaskId(null);
    setIsNewTaskMode(true);

    // 如果有初始数据，更新 getNewTaskInitialTime 的返回值
    if (initialData) {
      setNewTaskInitialData(initialData);
    } else {
      setNewTaskInitialData(null);
    }
  }, []);

  // 处理导航到聊天页面并聚焦输入框
  const handleNavigateToChatAndFocus = useCallback(() => {
    navigate('/v3/chat');
    focusChatInput();
  }, [navigate, focusChatInput]);

  // 处理导航到聊天页面并触发文件上传
  const handleNavigateToChatAndUpload = useCallback(() => {
    navigate('/v3/chat');
    setTimeout(() => {
      const uploadButton = document.querySelector('button[aria-label="添加附件"]') as HTMLButtonElement;
      if (uploadButton) {
        uploadButton.click();
      }
    }, 200);
  }, [navigate]);

  // 处理导航到聊天页面并切换到语音模式
  const handleNavigateToChatAndVoice = useCallback(() => {
    navigate('/v3/chat');
    setTimeout(() => {
      setVoiceMode(true);
    }, 100);
  }, [navigate, setVoiceMode]);



  // 关闭任务详情弹窗
  const handleCloseTaskDetail = useCallback(() => {
    setSelectedTaskId(null);
    setIsNewTaskMode(false);
    setNewTaskInitialData(null); // 清除初始数据
  }, []);

  // 处理 undo 操作
  const handleUndo = useCallback(async (taskId: string, previousState: boolean) => {
    try {
      await updateTask({
        id: taskId,
        done: previousState,
      });
    } catch (error) {
      console.error('TaskV2: Undo 操作失败:', error);
    }
  }, [updateTask]);

  // 处理欢迎项目创建成功
  const handleWelcomeProjectCreated = useCallback((projectId: string) => {
    console.log('[TaskV2] Welcome project created:', projectId);
  }, []);

  // 处理欢迎项目创建失败
  const handleWelcomeProjectError = useCallback((error: Error) => {
    console.error('[TaskV2] Welcome project creation failed:', error);
  }, []);

  // 渲染四象限展开模式
  const renderQuadrantExpandedView = useMemo(() => {
    const { categories, grouped } = tasksByImportance;

    return (
      <div className="divide-y divide-gray-200 dark:divide-ios-dark-gray4">
        {Object.entries(grouped).map(([key, tasks]) => {
          if (tasks.length === 0) return null;

          const category = categories[key as keyof typeof categories];

          return (
            <div key={key} className="py-4 first:pt-0 last:pb-0">
              {/* 分组标题 */}
              <div className={`
                px-4 py-2 mb-2
                ${isDark ? 'text-ios-dark-gray2' : 'text-gray-700'}
              `}>
                <h3 className="text-sm font-medium">
                  {t(`v3/tasks:importance.${category.label}`)}
                  <span className={`ml-2 text-xs ${
                    isDark ? 'text-ios-dark-gray3' : 'text-gray-500'
                  }`}>
                    ({tasks.length})
                  </span>
                </h3>
              </div>

              {/* 任务列表 */}
              <DroppableArea
                areaId={category.label}
                data={{
                  type: 'importance',
                  importance: {
                    vital: category.vital,
                    urgent: category.urgent,
                    label: category.label
                  }
                }}
              >
                <div className="divide-y divide-gray-100 dark:divide-ios-dark-gray5">
                  <TaskItemV2List
                    tasks={tasks}
                    onToggle={handleTaskToggle}
                    onClick={handleTaskClick}
                    onTaskUpdate={updateTask}
                    viewMode="list"
                    showBackground={false}
                    enableDrag={true}
                    dropZoneType="importance"
                  />
                </div>
              </DroppableArea>
            </div>
          );
        })}
      </div>
    );
  }, [tasksByImportance, isDark, t, handleTaskClick, handleTaskToggle, updateTask]);

  // 渲染四象限网格模式
  const renderQuadrantGridView = useMemo(() => {
    const { grouped } = tasksByImportance;

    // 定义四象限的顺序和样式
    const quadrants = [
      { key: 'vitalUrgent', title: '重要紧急', color: 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20' },
      { key: 'vital', title: '重要', color: 'border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-900/20' },
      { key: 'urgent', title: '紧急', color: 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20' },
      { key: 'other', title: '普通', color: 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50' }
    ];

    return (
      <div className="grid grid-cols-2 grid-rows-2 gap-4 p-4 h-full">
        {quadrants.map((quadrant) => {
          const tasks = grouped[quadrant.key as keyof typeof grouped] || [];
          const limitedTasks = tasks.slice(0, 10);
          const hasMoreTasks = tasks.length > 10;

          return (
            <DroppableArea
              key={quadrant.key}
              areaId={quadrant.key}
              data={{
                type: 'importance',
                importance: {
                  vital: quadrant.key === 'vital' || quadrant.key === 'vitalUrgent',
                  urgent: quadrant.key === 'urgent' || quadrant.key === 'vitalUrgent',
                  label: quadrant.key
                }
              }}
              className={`
                rounded-lg border p-4 overflow-hidden flex flex-col
                ${quadrant.color}
              `}
            >
              {/* 象限标题 */}
              <div className="flex items-center justify-between mb-3">
                <h4 className={`
                  text-sm font-medium
                  ${isDark ? 'text-ios-dark-gray1' : 'text-gray-900'}
                `}>
                  {quadrant.title}
                </h4>
                <span className={`
                  text-xs px-2 py-1 rounded-full
                  ${isDark ? 'bg-ios-dark-gray5 text-ios-dark-gray3' : 'bg-white text-gray-600'}
                `}>
                  {tasks.length}
                </span>
              </div>

              {/* 任务列表 */}
              <div className="flex-1 overflow-y-auto space-y-0">
                {limitedTasks.length > 0 ? (
                  <>
                    <TaskItemV2List
                      tasks={limitedTasks}
                      onToggle={handleTaskToggle}
                      onClick={handleTaskClick}
                      onTaskUpdate={updateTask}
                      compact={true}
                      viewMode="list"
                      showBackground={false}
                      enableDrag={true}
                      dropZoneType="importance"
                    />

                    {/* 更多任务提示 */}
                    {hasMoreTasks && (
                      <div className={`
                        px-2 py-2 text-center text-xs
                        ${isDark ? 'text-ios-dark-gray3' : 'text-gray-500'}
                      `}>
                        {t('v3/tasks:stats.moreTasksHint', {
                          count: tasks.length - 10,
                          defaultValue: `还有 {{count}} 个任务...`
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <div className={`
                    text-center py-4 text-xs
                    ${isDark ? 'text-ios-dark-gray4' : 'text-gray-400'}
                  `}>
                    {t('v3/tasks:emptyState.noTasks')}
                  </div>
                )}
              </div>
            </DroppableArea>
          );
        })}
      </div>
    );
  }, [tasksByImportance, isDark, t, handleTaskClick, handleTaskToggle, updateTask]);

  // TaskV2 页面特定的键盘快捷键处理 - 已迁移到新的快捷键系统
  // 旧的快捷键逻辑已被 useShortcutRegistration 和 useTaskV2ShortcutActions 替代
  useEffect(() => {
    // 保留注释作为参考，实际快捷键处理已迁移到新系统
    console.log('TaskV2: Old keyboard shortcuts disabled, using new shortcut system');

    // 旧的快捷键监听器已禁用，现在由新的快捷键系统处理
    // 所有快捷键逻辑已迁移到 useShortcutRegistration 和 useTaskV2ShortcutActions
  }, []);

  // 自动滚动到选中的任务（基于任务ID）
  useEffect(() => {
    if (focusedTaskId && scrollContainerRef.current) {
      const targetElement = scrollContainerRef.current.querySelector(`[data-task-id="${focusedTaskId}"]`) as HTMLElement;

      if (targetElement) {
        const container = scrollContainerRef.current;
        const containerRect = container.getBoundingClientRect();
        const targetRect = targetElement.getBoundingClientRect();

        // 检查元素是否在可视区域内
        const isVisible = targetRect.top >= containerRect.top &&
                         targetRect.bottom <= containerRect.bottom;

        if (!isVisible) {
          // 计算滚动位置，让目标元素在容器中央
          const scrollTop = targetElement.offsetTop - container.offsetTop -
                           (container.clientHeight / 2) + (targetElement.clientHeight / 2);

          container.scrollTo({
            top: Math.max(0, scrollTop),
            behavior: 'smooth'
          });
        }
      }
    }
  }, [focusedTaskId]);

  // 触摸手势支持 - 左右滑动切换日期
  useEffect(() => {
    if (!isTouchDevice() || specialMode) return; // 只在触摸设备且非特殊模式下启用

    let startX = 0;
    let startY = 0;
    let startTime = 0;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      startTime = Date.now();
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const touch = e.changedTouches[0];
      const endX = touch.clientX;
      const endY = touch.clientY;
      const endTime = Date.now();

      const deltaX = endX - startX;
      const deltaY = endY - startY;
      const deltaTime = endTime - startTime;

      // 手势检测条件
      const minSwipeDistance = 50; // 最小滑动距离
      const maxSwipeTime = 300; // 最大滑动时间（毫秒）
      const maxVerticalDistance = 100; // 最大垂直偏移

      // 检查是否为有效的水平滑动
      if (
        Math.abs(deltaX) > minSwipeDistance && // 水平距离足够
        Math.abs(deltaY) < maxVerticalDistance && // 垂直偏移不大
        deltaTime < maxSwipeTime && // 时间不超过限制
        Math.abs(deltaX) > Math.abs(deltaY) // 水平距离大于垂直距离
      ) {
        if (deltaX > 0) {
          // 右滑 - 前一天
          actions.goToPrevDay();
        } else {
          // 左滑 - 后一天
          actions.goToNextDay();
        }
      }
    };

    // 添加事件监听器到整个页面容器
    const container = document.querySelector('[data-view-mode]') as HTMLElement;
    if (container) {
      container.addEventListener('touchstart', handleTouchStart, { passive: true });
      container.addEventListener('touchend', handleTouchEnd, { passive: true });

      return () => {
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [specialMode, actions]);

  // 适配器函数：将 TaskDndContext 的参数转换为 handleNewTask 的参数
  const handleDndCreateTask = useCallback((params: any) => {
    const initialData: { startAt?: string; cost?: number } = {};

    // 如果有时间组信息，设置开始时间
    if (params.timeGroup) {
      const now = new Date();
      if (params.timeGroup === 'today') {
        initialData.startAt = now.toISOString();
      } else if (params.timeGroup === 'tomorrow') {
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        initialData.startAt = tomorrow.toISOString();
      } else if (params.timeGroup === 'yesterday') {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        initialData.startAt = yesterday.toISOString();
      }
    }

    handleNewTask(initialData);
  }, [handleNewTask]);

  // 适配器函数：将 TaskDndContext 的更新参数转换为 updateTask 的参数
  const handleDndUpdateTask = useCallback((taskId: string, params: any) => {
    const updateData: any = { id: taskId };

    // 处理项目更新
    if (params.projectId !== undefined) {
      updateData.projectId = params.projectId;
    }

    // 处理重要性更新
    if (params.importance) {
      updateData.vital = params.importance.vital;
      updateData.urgent = params.importance.urgent;
    }

    // 处理时间更新
    if (params.timeGroup) {
      const now = new Date();
      if (params.timeGroup === 'today') {
        updateData.startAt = now.toISOString();
      } else if (params.timeGroup === 'tomorrow') {
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        updateData.startAt = tomorrow.toISOString();
      } else if (params.timeGroup === 'yesterday') {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        updateData.startAt = yesterday.toISOString();
      }
    }

    updateTask(updateData);
  }, [updateTask]);

  return (
    <TaskDndContext
      onCreateTask={handleDndCreateTask}
      onUpdateTask={handleDndUpdateTask}
    >
      <div
        className={`flex flex-col h-screen ${isDark ? 'bg-ios-dark-gray6' : 'bg-gray-50'}`}
        data-view-mode={viewMode}
        data-special-mode={specialMode}
      >
      {/* 顶部导航 */}
      <TaskV2Header
        currentDate={currentDate}
        viewMode={viewMode}
        showNoTimeTasks={showNoTimeTasks}
        specialMode={specialMode}
        allTasks={allTasksWithProjects}
        onDateChange={setCurrentDate}
        onViewModeChange={setViewMode}
        onShowNoTimeTasksChange={setShowNoTimeTasks}
        onSpecialModeChange={setSpecialMode}
      />

      {/* 任务列表区域 - 添加顶部间距以避让固定的 header */}
      <div
        ref={scrollContainerRef}
        data-scroll-container
        className={`flex-1 overflow-auto pt-[calc(4rem+env(safe-area-inset-top))] ${layoutMode === 'desktop' ? 'pb-4' : 'pb-32'}`}
      >
        {viewMode === 'quadrant' ? (
          // 四象限网格视图
          renderQuadrantGridView
        ) : (
          // 其他视图模式使用TaskV2List
          <TaskV2List
            tasks={tasksWithProjects}
            viewMode={viewMode === 'timeline' ? 'timeline' : viewMode}
            specialMode={specialMode}
            currentDate={currentDate}
            stackMode={false}
            allTasks={allTasksWithProjects}
            onTaskClick={handleTaskClick}
            onTaskToggle={handleTaskToggle}
            onTaskUpdate={updateTask}
            onDateChange={setCurrentDate}
            onNewTask={handleNewTask}
          />
        )}
      </div>

      {/* 时间轴缩放控制器 - 只在时间轴模式下显示 */}
      {viewMode === 'timeline' && !specialMode && (
        <TimelineScaleController />
      )}

      {/* 底部操作区域 */}
      {layoutMode === 'desktop' ? (
        /* 桌面模式：使用FAB */
        <DraggableTaskButton onClick={() => handleNewTask()} />
      ) : (
        /* 移动模式：使用ChatInputContainer的quickbarMode */
        <ChatInputContainer
          quickbarMode={true}
          onNewTask={() => handleNewTask()}
          onFocusNavigateToChat={handleNavigateToChatAndFocus}
          onFileUploadNavigateToChat={handleNavigateToChatAndUpload}
          onVoiceNavigateToChat={handleNavigateToChatAndVoice}
        />
      )}

      {/* 任务详情弹窗 */}
      <TaskDetailSheet
        taskId={selectedTaskId}
        initialData={isNewTaskMode ? getNewTaskInitialData() : undefined}
        isOpen={!!(selectedTaskId || isNewTaskMode)}
        onClose={handleCloseTaskDetail}
      />

      {/* Undo Toast */}
      <UndoToastContainer onUndo={handleUndo} />

      {/* 欢迎项目创建器 - 逻辑组件，不渲染UI */}
      <WelcomeProjectCreator
        onProjectCreated={handleWelcomeProjectCreated}
        onError={handleWelcomeProjectError}
      />

      {/* App 引导组件 */}
      {needsOnboarding && (
        <AppOnboarding
          isFirstTime={isFirstTime}
          autoStart={true}
          onComplete={completeOnboarding}
          onSkip={skipOnboarding}
        />
      )}

    </div>
    </TaskDndContext>
  );
};
